import User from '@models/user/User';

export const AuthenticationSubscriptionService = createAuthenticationSubscriptionService();

export function createAuthenticationSubscriptionService() {
    const postLoginHandlers: PostLoginHandler[] = [];
    const postLogoutHandlers: PostLogoutHandler[] = [];
    let loggedInUser: User | undefined;
    let runLogoutHandlers = false;

    function registerPostLoginHandler(handler: PostLoginHandler): void {
        postLoginHandlers.push(handler);

        // In case we were pre-logged in by the time an external handler has
        // registered itself, we need to invoke it immediately
        if (loggedInUser !== undefined) {
            handler(loggedInUser);
        }
    }

    function registerPostLogoutHandler(handler: PostLogoutHandler): void {
        postLogoutHandlers.push(handler);

        // In case it registered late but might need to run some cleanup, perform it.
        if (runLogoutHandlers) {
            handler();
        }
    }

    function callPostLoginHandlers(user: User): Promise<unknown> {
        // If we were logged out before, we should no longer run post-logout handlers now
        runLogoutHandlers = false;

        // Store the user for future use, in case some handlers are registered after
        // logging in due to, for example, a race condition
        loggedInUser = user;

        return Promise.all(postLoginHandlers.map((handler) => handler(user)));
    }

    async function callPostLogoutHandlers(): Promise<void> {
        await Promise.all(postLogoutHandlers.map(async (handler) => {
            await handler();
        }));

        // Clear out the logged in user to prevent stale data usage
        loggedInUser = undefined;
        runLogoutHandlers = true;
    }

    return {
        registerPostLoginHandler,
        registerPostLogoutHandler,
        callPostLoginHandlers,
        callPostLogoutHandlers,
    };
}

type PostLoginHandler = (user: User) => void | Promise<void>;
type PostLogoutHandler = () => void | Promise<void>;
